import React, { useContext, useEffect, useState } from 'react';
import { gql, useQuery, useLazyQuery } from '@apollo/client';
import {
  Box,
  Grid,
  Button,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Autocomplete,
  TextField,
} from '@mui/material';
import { useSelector } from 'react-redux';

import { UnitContext } from '../../../../context/UnitContext';
import { useAppDispatch } from '../../../../app/hooks';
import { selectProject } from '../../../../features/project/projectSlice';
import { showErrorSnackbar } from '../../../../features/snackbar/snackbarSlice';
import DealDeposits from '../../DealDeposits';
import DealOptions from '../../DealOptions';
import Dropdown from '../../../common/formControls/Dropdown';
import TextInput from '../../../common/formControls/TextInput';
import { IUnit } from '../../../../types/unit';
import { IDocuments } from '../../../../types/docusign';
import { IMerge, IMergeData } from '../../../../types/merge';
import { numToCurrency, createPdf, sortSuites, getSalesStatusCodes } from '../../../../utils/Functions';
import { IDealDeposit } from '../../../../types/CreateDealForm';
import { IOption } from '../../../../types/project';
import { DealContext } from '../../../../context/DealContext';
import { IDealOption } from '../../../../types/CreateDealForm';

const UnitTransfer = () => {
  const { unit, filteredDeal, assignment } = useContext(UnitContext);
  const {
    handleOptionDropdown,
    handleOptionInput,
    optionValue,
    depositList,
    selectedDeposit,
    setSelectedDeposit,
    pendingOptions,
    setPendingOptions,
    unitTransferTotal,
    setUnitTransferTotal,
    unitTransfer,
    setUnitTransfer,
    pendingDeposits,
    setPendingDeposits,
    document,
    rental,
    setRental,
    setDocument,
    keepDeposits,
    setKeepDeposits,
    createDepositAmendment,
  } = useContext(DealContext);
  const storeDispatch = useAppDispatch();
  const project = useSelector(selectProject);

  const [merges, setMerges] = useState<any[]>([]);
  const [otherMerges, setOtherMerges] = useState<any[]>([]);
  const [availableUnits, setAvailableUnits] = useState<IUnit[]>([]);
  const [selectedMergeTemplates, setSelectedMergeTemplates] = useState<IMerge[]>([]);
  const [selectedUnitTransferMerge, setSelectedUnitTransferMerge] = useState<IMerge | null>(null);
  const [removePlan, setRemovePlan] = useState<boolean>(false);

  const [getUnitTransferMerges] = useLazyQuery<IMergeData>(MERGETEMPLATES, {
    variables: { filter: { project: project._id, OR: [{ type: 'UnitTransfer' }, { type: 'Other' }] } },
    onCompleted: (data) => {
      setMerges(data.mergeTemplateMany.filter((mergeFields: IMerge) => mergeFields.type === 'UnitTransfer'));
      setOtherMerges(data.mergeTemplateMany.filter((mergeFields: IMerge) => mergeFields.type === 'Other'));
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const { loading } = useQuery(UNITS, {
    variables: { filter: { project: project._id } },
    onCompleted: (data) => {
      getUnitTransferMerges();
      // Sales-category statuses other than Conditional get the same self-exception F used to get
      // alone - the unit currently under this deal keeps its own sold status but must still show
      // up as an option (Conditional is excluded outright, same as before).
      const salesStatusCodesExcludingC = getSalesStatusCodes(project).filter((code: string) => code !== 'C');
      let availableUnits = data.unitMany.filter((selectedUnit: IUnit) => {
        return (
          selectedUnit.status !== 'C' &&
          (!salesStatusCodesExcludingC.includes(selectedUnit.status) || selectedUnit.suite === unit.suite) &&
          selectedUnit.status !== 'S' &&
          selectedUnit.status !== 'O' &&
          selectedUnit.status !== 'IP' &&
          selectedUnit.status !== 'P' &&
          selectedUnit.status !== 'UT'
        );
      });
      setAvailableUnits(sortSuites(availableUnits, 'suite'));
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  // Functions

  useEffect(() => {
    if (unitTransfer && unitTransfer.hasOwnProperty('rental') && unitTransfer.rental !== null) {
      setRental(
        pendingOptions.reduce((a: any, b: any) => {
          return a + b.rental * b.purchaseAmount;
        }, unitTransfer.rental)
      );
    }
  }, [pendingOptions, unitTransfer]);

  const handleUnitTransferInput = (event: React.ChangeEvent<{ name?: string; value: string }>) => {
    let mergeArray = merges.find((mergeTemplate: IMerge) => mergeTemplate.name === event.target.value);
    if (mergeArray) {
      setSelectedUnitTransferMerge(mergeArray);
    } else {
      storeDispatch(showErrorSnackbar(`Error selecting merge template`));
    }
  };

  const availableSuiteNames = () => {
    let suite = availableUnits.map((unit: IUnit, index: number) => {
      return unit.suite;
    });
    return suite;
  };

  const handleUnitDropdown = (event: React.ChangeEvent<{ name?: string; value: string }>) => {
    let selectedUnit: any = availableUnits.find((selectedUnit: IUnit) => {
      return selectedUnit.suite === event.target.value;
    });
    let allOptions: any[] = [];
    if (selectedUnit) {
      allOptions = project.options.map((option: any) => {
        let selectedOption = filteredDeal.options.find((dealOption: IDealOption) => option.name === dealOption.name);
        return {
          name: option.name,
          purchaseAmount: selectedOption ? selectedOption.purchaseAmount : 0,
          amount: selectedOption ? selectedOption.amount : 0,
          rental: option.rental,
        };
      });
      setDocument('UnitTransfer');
      setPendingOptions(allOptions);
    }

    let totalOptions = allOptions.reduce((a: any, b: any) => {
      return a + b.amount;
    }, selectedUnit.basePrice);

    setUnitTransfer(selectedUnit);
    setUnitTransferTotal(totalOptions);
  };

  const handlePendingDepositsArray = (array: IDealDeposit[]) => {
    setPendingDeposits(array);
  };

  const handleUnitTransfer = async (e: any) => {
    e.preventDefault();
    if (!selectedUnitTransferMerge) return storeDispatch(showErrorSnackbar(`Unit Transfer Template Not Selected`));

    if (!keepDeposits && !selectedDeposit) {
      storeDispatch(showErrorSnackbar(`No Deposit Selected`));
      return;
    }

    if (!unitTransfer?.floorPlan) {
      storeDispatch(showErrorSnackbar(`No Floorplan Merge found`));
      return;
    }

    if (filteredDeal.pendingAddPurchasers.length || filteredDeal.pendingDeletePurchasers.length) {
      storeDispatch(showErrorSnackbar(`Please remove Pending Purchasers`));
      return;
    }

    let pdf = await createPdf(
      selectedUnitTransferMerge,
      project,
      { ...unit, rental: filteredDeal.pendingRental ? filteredDeal.pendingRental : unit.rental ? unit.rental : null },
      assignment ? assignment.assignees : filteredDeal.purchasers,
      [],
      [],
      filteredDeal.deposit,
      filteredDeal.options,
      null,
      { ...unitTransfer, rental: rental ? rental : unitTransfer.rental ? unitTransfer.rental : null },
      pendingOptions,
      pendingDeposits,
      filteredDeal,
      null,
      false,
      selectedMergeTemplates,
      [],
      removePlan
    );

    const args = {
      status: 'Prepared',
      name: selectedUnitTransferMerge.name,
      project: project._id,
      deal: filteredDeal._id,
      // signers,
      type: 'UnitTransfer',
      isAPS: false,
      mergeTemplate: [
        selectedUnitTransferMerge._id,
        unitTransfer.floorPlan._id,
        selectedMergeTemplates.map((mergeTemplate: IMerge) => mergeTemplate._id)
      ].flat()
    };

    let newDeposit = pendingDeposits.map((pendingDeposits: IDealDeposit) => {
      let chequeType =
        pendingDeposits.dateType === '90 Days Before Firm Occupancy' ||
        pendingDeposits.dateType === '120 Days Before Firm Occupancy' ||
        pendingDeposits.dateType === '180 Days Before Firm Occupancy' ||
        pendingDeposits.dateType === 'Floating Date'
          ? ' floating'
          : '';
      return {
        accountNumber: pendingDeposits.accountNumber,
        amount: pendingDeposits.amount,
        deal: filteredDeal._id,
        dueDate: pendingDeposits.dueDate,
        name: pendingDeposits.name,
        chequeType: chequeType,
      };
    });

    let newOptions = pendingOptions.map((pendingOption: any) => {
      return {
        ...pendingOption,
        purchaseAmount: parseFloat(pendingOption.purchaseAmount),
      };
    });

    setPendingOptions(newOptions);

    let removeEmptyOptions: any[] = [];

    if (newOptions.length > 0) {
      removeEmptyOptions = newOptions.filter((options: any) => options.purchaseAmount).map(({ rental, ...attrs }: any) => attrs);
    }

    await createDepositAmendment({
      variables: {
        type: 'unitTransfer',
        dealId: filteredDeal._id,
        records: pendingDeposits.length ? newDeposit : [],
        record: args,
        file: pdf,
        pendingBasePrice: null,
        pendingRental: rental ? parseInt(rental, 10) : null,
        depositName: selectedDeposit,
        options: removeEmptyOptions,
        unitId: unit._id,
        unitTransferId: unitTransfer._id,
      },
    });
  };

  const totalOptionsPrice = () => {
    let totalOptions = filteredDeal.pendingUnit.basePrice;
    if (filteredDeal.pendingOptions.length > 0) {
      totalOptions = filteredDeal.pendingOptions.reduce((a: any, b: any) => {
        return a + b.amount;
      }, filteredDeal.pendingUnit.basePrice);
    } else if (pendingOptions.length > 0) {
      totalOptions = pendingOptions.reduce((a: any, b: any) => {
        return a + b.amount;
      }, filteredDeal.pendingUnit.basePrice);
    }
    return totalOptions;
  };

  const rentalCalculation = () => {
    if (pendingOptions.length) {
      let totalRental = pendingOptions.reduce((a: any, b: any) => {
        return a + b.rental * b.purchaseAmount;
      }, 0);
      return totalRental;
    } else return 0;
  };

  return (
    <div>
      <div>
        <Box sx={{ mb: 3, mt: 1 }}>
          Please note that the deal will still be able to be viewed under this unit until the unit transfer has been executed. You will only
          be able to a transfer to a unit that is available. Please confirm with management to proceed.
        </Box>
        {filteredDeal.documents.find(
          (document: IDocuments) =>
            (document.type === 'UnitTransfer' && document.status !== 'Completed') ||
            (document.type === 'Options' && document.status !== 'Completed')
        ) ? (
          <div>
            <strong>
              There is currently a Unit Transfer/Deposit Amendment active. Please void/delete it to create a new Unit Transfer Amendment
            </strong>
            {filteredDeal.pendingOptions.length > 0 && document === 'UnitTransfer' ? (
              <div>
                <h3
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  <em>Pending Options</em>
                </h3>
                <p>
                  <em>
                    These options are currently pending until the Unit Transfer Amendment has been executed. After the amendment has been
                    executed, the unit transfer will be complete.
                  </em>
                </p>
                <p>
                  <em>
                    WARNING: If the unit price for the new unit has been changed and the amendment has been sent with the old price, you
                    will have to void the amendment and send a new one with the new price.
                  </em>
                </p>
                <Box sx={{ mt: 3 }}>
                  <div>
                    Unit Transfer Suite: <strong>{filteredDeal.pendingUnit.suite}</strong>
                  </div>
                  <div>
                    Unit Transfer Base Price: <strong>{numToCurrency.format(filteredDeal.pendingUnit.basePrice)}</strong>
                  </div>
                  <div>
                    Total Price with Pending Options: <strong>{numToCurrency.format(totalOptionsPrice())}</strong>
                  </div>
                </Box>
                <DealOptions options={filteredDeal.pendingOptions} disabled={true} />
              </div>
            ) : null}
          </div>
        ) : (
          <form id="unit-transfer" onSubmit={(e: any) => handleUnitTransfer(e)}>
            <FormControl fullWidth>
              <InputLabel id="id-question-label">Select a Unit to Transfer to</InputLabel>
              <Select
                value={unitTransfer ? unitTransfer.suite : ''}
                labelId="id-executor-label"
                id="id-executor"
                label="Select a Unit to Transfer to"
                name="unitTransfer"
                onChange={(event: any) => handleUnitDropdown(event)}
              >
                {loading ? (
                  <MenuItem disabled={true}>Loading...</MenuItem>
                ) : (
                  availableSuiteNames().map((suite: string) => {
                    return <MenuItem value={suite}>{suite}</MenuItem>;
                  })
                )}
              </Select>
            </FormControl>
            {unitTransfer ? (
              <div>
                <Box sx={{ mt: 3 }}>
                  <div>
                    Unit Price: <strong>{numToCurrency.format(unitTransfer.basePrice)}</strong>
                  </div>
                  <div>
                    Total Price: <strong>{numToCurrency.format(unitTransferTotal)}</strong>
                  </div>
                  <div>
                    Model: <strong>{unitTransfer.modelType}</strong>
                  </div>
                  <div>
                    Unit Type: <strong>{unitTransfer.unitType}</strong>
                  </div>
                </Box>
                <Grid sx={{ mt: 3 }} container spacing={2}>
                  {project.options.map((option: IOption, index: number) => {
                    return (
                      <React.Fragment key={index}>
                        <Grid
                          sx={{
                            alignSelf: 'center',
                            fontSize: '20px',
                            '@media (max-width:1499.98px)': { fontSize: '16px' },
                          }}
                          item
                          xs={12}
                          sm={3}
                        >
                          <div>
                            <strong>{option.name}</strong>
                          </div>
                        </Grid>
                        <Grid item xs={12} sm={5}>
                          <Dropdown
                            title={`Amount`}
                            menuList={['0', '1', '2', '3']}
                            name={'purchaseAmount'}
                            handleSelect={(e: any) => handleOptionDropdown(e, index, option.name)}
                            value={optionValue(option.name, 'purchaseAmount', index)}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextInput
                            title={`${option.name} Total Amount`}
                            type="number"
                            id={'totalAmount'}
                            name={'totalAmount'}
                            handleTextInput={(e: any) => handleOptionInput(e, option.name)}
                            value={optionValue(option.name, 'amount', index)}
                            adornment={'$'}
                            required={true}
                          />
                        </Grid>
                      </React.Fragment>
                    );
                  })}
                  {unitTransfer.hasOwnProperty('rental') && unitTransfer.rental !== null ? (
                    <>
                      <Grid
                        sx={{
                          alignSelf: 'center',
                          fontSize: '20px',
                          '@media (max-width:1499.98px)': { fontSize: '16px' },
                        }}
                        item
                        xs={12}
                        sm={3}
                      >
                        <div>
                          <strong>Rental</strong>
                        </div>
                      </Grid>
                      <Grid item xs={12} sm={5}>
                        <TextInput
                          title={`Total Rental Guarantee Amount`}
                          type="number"
                          id={'rental'}
                          handleTextInput={(e: any) => {
                            setRental(e.target.value);
                          }}
                          helperText={
                            rental &&
                            pendingOptions.length &&
                            rental ===
                              pendingOptions.reduce((a: any, b: any) => {
                                return a + b.rental * b.purchaseAmount;
                              }, unitTransfer.rental)
                              ? `This Rental Guarantee has increase by ${numToCurrency.format(rentalCalculation())}`
                              : ''
                          }
                          value={rental}
                          min={'0'}
                          adornment={'$'}
                          required={false}
                        />
                      </Grid>
                    </>
                  ) : null}
                </Grid>
                <Box>
                  <FormControlLabel
                    control={<Checkbox color="primary" checked={keepDeposits} onClick={() => setKeepDeposits(!keepDeposits)} />}
                    label={'Keep Deposit Structure Amounts'}
                  />
                </Box>
                {!keepDeposits ? (
                  <>
                    <Box sx={{ mt: 2 }}>
                      <Dropdown
                        title={'Deposit Type'}
                        id={'depositType'}
                        menuList={depositList()}
                        name={'depositType'}
                        handleSelect={(event: any) => setSelectedDeposit(event.target.value)}
                        value={selectedDeposit}
                      />
                    </Box>
                    <DealDeposits
                      deposit={pendingDeposits}
                      basePrice={unitTransferTotal}
                      handleDepositsArray={handlePendingDepositsArray}
                      disable={false}
                    />
                  </>
                ) : null}
                <h2>Select a Unit Transfer Amendment</h2>
                <Dropdown
                  title={'Unit Transfer Amendment'}
                  menuList={merges.map((merge: any) => merge.name)}
                  name={'options'}
                  handleSelect={(e: any) => handleUnitTransferInput(e)}
                  value={selectedUnitTransferMerge ? selectedUnitTransferMerge.name : ''}
                />
                <Box>
                  <FormControlLabel
                    style={{
                      marginTop: '10px',
                      marginBottom: '15px',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                    key={`removePlan`}
                    control={<Checkbox checked={removePlan} onChange={() => setRemovePlan(!removePlan)} />}
                    label={'Remove Floorplan from Amendment'}
                  />
                </Box>
                <Autocomplete
                  multiple
                  options={otherMerges}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) => option === value}
                  disableClearable={false}
                  freeSolo={false}
                  value={selectedMergeTemplates}
                  onChange={(event: any, newValue: any | null) => {
                    setSelectedMergeTemplates(newValue.map((option: string) => option));
                  }}
                  renderInput={(params) => <TextField {...params} label="Attach Additional Amendments/Schedules" size="medium" />}
                />
                <Box sx={{ mt: 3 }}>
                  <Button form="unit-transfer" type="submit" color="primary" variant="contained">
                    Create Unit Transfer Amendment
                  </Button>
                </Box>
              </div>
            ) : null}
          </form>
        )}
      </div>
    </div>
  );
};
const UNITS = gql`
  query unitMany($filter: FilterFindManyUnitInput) {
    unitMany(filter: $filter, limit: 10000) {
      _id
      suite
      unit
      level
      tier
      modelType
      unitType
      bathroom
      size
      basePrice
      col
      row
      rental
      maintenanceFee
      status
      getUrl
      floorPlan {
        _id
        mergeFields {
          key
          index
          pageNumber
          x
          y
          fontSize
          format
          wrap
        }
        signFields {
          key
          index
          pageNumber
          x
          y
          title
        }
      }
      history {
        type
        description
        timestamp
        _id
        user {
          _id
          fullName
        }
      }
    }
  }
`;

const MERGETEMPLATES = gql`
  query mergeTemplateMany($filter: FilterFindManyMergeTemplateInput) {
    mergeTemplateMany(filter: $filter) {
      _id
      project {
        _id
      }
      name
      mergeFields {
        key
        index
        pageNumber
        x
        y
        fontSize
        format
        wrap
      }
      signFields {
        key
        index
        pageNumber
        x
        y
        name
        title
      }
      default
      totalPages
      type
      getUrl
      putUrl
    }
  }
`;

export default UnitTransfer;
